import {
  FC,
  useState,
  useEffect,
  useRef,
  CSSProperties,
  ReactElement,
} from 'react';
import { AlignLeftOutlined } from '@ant-design/icons';
import { ProSelect, ProInput, ProForm, ProButton, ProSpace } from '../antd';
import classnames from 'classnames';
import useClickOutside from '../Hooks/useClickOutside';
import { strToTags } from './util';
import { TextAreaRowProps } from './interface';
import { MAX_TAG_TEXT_LENGTH } from '../constants/constant';
import styles from './style.module.less';

const dropdownStyle: CSSProperties = { minWidth: 200 };
/** 多行表单 */
const TextAreaRow: FC<TextAreaRowProps> = props => {
  const {
    className,
    style,
    selectStyle,
    value,
    maxRow,
    addonBefore,
    suffixIcon,
    isUpperCase,
    dropdownHeader,
    onChange,
    ...restProps
  } = props;
  const [selectValue, setSelectValue] = useState<string[]>();
  const [open, setOpen] = useState<boolean>(false);
  const [form] = ProForm.useForm();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<any>(null);
  const textAreaRef = useRef<HTMLElement>(null);
  useClickOutside([dropdownRef, wrapperRef], open, () => {
    setOpen(false);
  });
  useEffect(() => {
    setSelectValue(value);
  }, [value]);
  const classes = classnames(styles.wrapper, className);
  // 选项改变
  const handleChange = (value: any, option: any) => {
    setSelectValue(value);
    onChange?.(value, option);
  };
  // 获取焦点
  const handleFoucs = () => {
    if (open) return;
    setOpen(true);
    let textAreaValue = selectValue?.join?.('\r\n');
    if (textAreaValue) textAreaValue = `${textAreaValue}\r\n`;
    form.setFieldsValue({ textArea: textAreaValue });
    selectRef.current?.blur();
    setTimeout(() => {
      textAreaRef.current!.focus();
    });
  };
  // 确定
  const handleOk = () => {
    form.submit();
  };
  const handleSuffixIcon = () => {
    handleFoucs();
  };
  // 提交
  const handleFinish = (values: any) => {
    const { textArea } = values;
    const newSelectValue: string[] = strToTags(textArea);
    setSelectValue(newSelectValue);
    onChange?.(newSelectValue, []);
    setOpen(false);
  };
  // 自定义下拉框内容
  const handleDropdownRender = (originNode: ReactElement) => {
    if (open) {
      return (
        <div ref={dropdownRef}>
          {dropdownHeader}
          <ProForm component={false} form={form} onFinish={handleFinish}>
            <ProForm.Item
              name="textArea"
              rules={[
                () => ({
                  validator(_, value) {
                    const newSelectValue: string[] = strToTags(value);
                    if (maxRow && newSelectValue.length > maxRow) {
                      return Promise.reject(new Error(`最多输入${maxRow}行!`));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <ProInput.TextArea
                ref={textAreaRef}
                rows={6}
                placeholder={`一行一项，最多支持${maxRow}行`}
                isUpperCase={isUpperCase}
                onKeyDown={(e: any) => {
                  if (e.keyCode === 13) {
                    const prevTextAreaValue = form.getFieldValue('textArea');
                    const nextTextAreaValue = `${prevTextAreaValue || ''}\r\n`;
                    form.setFieldsValue({ textArea: nextTextAreaValue });
                  }
                }}
              />
            </ProForm.Item>
            <div className={styles.btn}>
              <ProButton
                type="default"
                size="small"
                onClick={() => form.resetFields()}
              >
                清空
              </ProButton>
              <ProSpace>
                <ProButton
                  type="default"
                  size="small"
                  onClick={() => setOpen(false)}
                >
                  关闭
                </ProButton>
                <ProButton type="primary" size="small" onClick={handleOk}>
                  确定
                </ProButton>
              </ProSpace>
            </div>
          </ProForm>
        </div>
      );
    }
    return originNode;
  };
  return (
    <div className={classes} style={style} ref={wrapperRef}>
      {!!addonBefore && <div>{addonBefore}</div>}
      <ProSelect
        {...restProps}
        style={selectStyle}
        ref={selectRef}
        className={styles.select}
        mode="tags"
        showArrow={true}
        dropdownStyle={dropdownStyle}
        value={selectValue}
        open={open}
        virtual={false}
        maxTagPlaceholder={omittedValues => {
          return `+${omittedValues.length}`;
        }}
        suffixIcon={
          suffixIcon ? (
            <span onClick={handleSuffixIcon}>{suffixIcon}</span>
          ) : null
        }
        dropdownRender={handleDropdownRender}
        onChange={handleChange}
        onFocus={handleFoucs}
      />
    </div>
  );
};

TextAreaRow.defaultProps = {
  maxTagCount: 1,
  maxTagTextLength: MAX_TAG_TEXT_LENGTH,
  maxRow: 500,
  suffixIcon: <AlignLeftOutlined />,
};

export default TextAreaRow;
